import { NavLink, Outlet } from 'react-router-dom';
import type { ComponentType } from 'react';
import { LogOut } from 'lucide-react';
import { useAuth } from './AuthProvider';
import NotificationBell from './NotificationBell';
import { ThemeSwitch } from './ThemeProvider';
import styles from './AppShell.module.css';

export type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number; strokeWidth?: number }>;
  /** Only match this exact path (used for area index routes). */
  end?: boolean;
};

type AppShellProps = {
  items: NavItem[];
  logo: string;
  /** Drives the in-app notification bell; admins have no feed. */
  bellRole?: 'client' | 'provider';
  areaLabel: string;
};

export default function AppShell({ items, logo, bellRole, areaLabel }: AppShellProps) {
  const { signOut } = useAuth();

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHead}>
          <div className={styles.sidebarBrand}>
            <img className={styles.sidebarLogo} src={logo} alt="DABA" />
            {bellRole ? <NotificationBell role={bellRole} align="left" /> : null}
          </div>
          <span className={styles.areaLabel}>{areaLabel}</span>
        </div>

        <nav className={styles.sidebarNav} aria-label="Navigation principale">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                isActive ? `${styles.sidebarLink} ${styles.sidebarLinkActive}` : styles.sidebarLink
              }
            >
              <Icon size={21} strokeWidth={2.1} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className={styles.sidebarTheme}>
          <span className={styles.sidebarThemeLabel}>Apparence</span>
          <ThemeSwitch />
        </div>

        <button type="button" className={styles.sidebarSignOut} onClick={() => void signOut()}>
          <LogOut size={18} />
          <span>Déconnexion</span>
        </button>
      </aside>

      <div className={styles.main}>
        <header className={styles.mobileBar}>
          <img className={styles.mobileLogo} src={logo} alt="DABA" />
          {bellRole ? <NotificationBell role={bellRole} /> : null}
        </header>

        <main className={styles.content}>
          <Outlet />
        </main>

        <nav className={styles.tabbar} aria-label="Navigation">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                isActive ? `${styles.tab} ${styles.tabActive}` : styles.tab
              }
            >
              <span className={styles.tabIcon}>
                <Icon size={22} strokeWidth={2.1} />
              </span>
              <span className={styles.tabLabel}>{label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
